import { IntlShape } from 'react-intl';

export type ScreeningEvaluationValue = 0 | 1 | 2 | 3 | 4;

export type ScreeningBackendEvaluationState =
  | 'unreviewed'
  | 'considered'
  | 'conditional'
  | 'selected'
  | 'rejected'
  | 'priority';

export type ScreeningFilterValue =
  | 'all'
  | 'undecided'
  | 'accepted'
  | 'needs-attention'
  | 'rejected';

export type ScreeningBinaryReviewDashboard = {
  total_reviews: number;
  binary_positive_count: number;
  binary_negative_count: number;
  total_comment: number;
  last_review_time: number;
  last_calculated_time: number;
};

export type ScreeningOfferLike = {
  id: number;
  state?: number;
  created_by_name?: string;
  contract_id: number;
  offeror_identity_information?: {
    name?: string;
    email?: string;
    company?: string;
  } | null;
  vendor_information?: {
    company_name?: string | null;
    contact_name?: string | null;
    contact_email?: string | null;
    company_main_email?: string | null;
  } | null;

  screening?: {
    state?: ScreeningEvaluationValue | ScreeningBackendEvaluationState | null;
    is_accepted?: boolean | null;
    screening_binary_review_dashboard?: ScreeningBinaryReviewDashboard | null;
  } | null;

  evaluation?: {
    state?: ScreeningEvaluationValue | ScreeningBackendEvaluationState | null;
  } | null;

  score?: {
    delivery_current?: number | null;
    delivery_max?: number | null;
    optional_current?: number | null;
    optional_max?: number | null;
  } | null;
};

export type ScreeningContractLike = {
  id: number;
  name?: string;
  description?: string | null;
  reference?: string | null;
  state?: number;
};

export type ScreeningVoucherLike = {
  id: number;
  tender_voucher_id?: number;
  name?: string;
  description?: string | null;
  voucher_type?: number;
  is_optional?: boolean;
  attachments?: any[];
  unit_count?: number | null;
  unit_type?: number | null;
};

export type ScreeningVoucherResponseLike = {
  voucher_id?: number;
  voucher_type?: number;
  sum?: number | null;
  is_approved?: boolean | null;
  attachments?: any[];
};

export type ScreeningMergedVoucher = {
  id: number;
  name: string;
  description?: string | null;
  voucherType?: number;
  isOptional: boolean;
  isDelivered: boolean;
  projectAttachments: any[];
  vendorAttachments: any[];
  unitCount?: number | null;
  unitType?: number | null;
  response?: ScreeningVoucherResponseLike;
};

export type ScreeningScore = {
  delivered: number;
  total: number;
};

export type ScreeningScores = {
  required: ScreeningScore;
  optional: ScreeningScore;
};

export type ScreeningOfferData = {
  offer_id: number;
  contract_id: number;
  voucher_response?: {
    required_total: number;
    required_responded: number;
    optional_total: number;
    optional_responded: number;
  } | null;
};

export const getScoresFromOfferData = (
  data: ScreeningOfferData | undefined
): ScreeningScores => ({
  required: {
    delivered: data?.voucher_response?.required_responded ?? 0,
    total: data?.voucher_response?.required_total ?? 0,
  },
  optional: {
    delivered: data?.voucher_response?.optional_responded ?? 0,
    total: data?.voucher_response?.optional_total ?? 0,
  },
});

export const getOfferScreeningValue = (
  offer: ScreeningOfferLike
): ScreeningEvaluationValue => {
  const screeningState = offer.screening?.state;

  if (screeningState === 3 || screeningState === 'selected') {
    return 3;
  }

  if (screeningState === 4 || screeningState === 'rejected') {
    return 4;
  }

  return 0;
};

export const getOfferScreeningScores = (
  offer: ScreeningOfferLike
): ScreeningScores => {
  return {
    required: {
      delivered: Number(offer.score?.delivery_current ?? 0),
      total: Number(offer.score?.delivery_max ?? 0),
    },
    optional: {
      delivered: Number(offer.score?.optional_current ?? 0),
      total: Number(offer.score?.optional_max ?? 0),
    },
  };
};

export const SCREENING_EVALUATION_OPTIONS: {
  value: ScreeningEvaluationValue;
  label: string;
  color: string;
}[] = [
  {
    value: 0,
    label: 'Undecided',
    color: '#8c8c8c',
  },
  {
    value: 1,
    label: 'Needs attention',
    color: '#faad14',
  },
  {
    value: 2,
    label: 'Incomplete',
    color: '#fa8c16',
  },
  {
    value: 3,
    label: 'Accepted',
    color: '#52c41a',
  },
  {
    value: 4,
    label: 'Rejected',
    color: '#ff4d4f',
  },
];

export const SCREENING_FILTER_OPTIONS: {
  value: ScreeningFilterValue;
  label: string;
}[] = [
  {
    value: 'all',
    label: 'All deliveries',
  },
  {
    value: 'undecided',
    label: 'Undecided',
  },
  {
    value: 'accepted',
    label: 'Accepted',
  },
  {
    value: 'needs-attention',
    label: 'Needs attention',
  },
  {
    value: 'rejected',
    label: 'Rejected',
  },
];

export const getScreeningEvaluationOption = (
  value?: ScreeningEvaluationValue | null
) => {
  return (
    SCREENING_EVALUATION_OPTIONS.find((option) => option.value === value) ||
    SCREENING_EVALUATION_OPTIONS[0]
  );
};

export const getScreeningEvaluationFilterValue = (
  value?: ScreeningEvaluationValue | null
): ScreeningFilterValue => {
  switch (value) {
    case 3:
      return 'accepted';
    case 1:
    case 2:
      return 'needs-attention';
    case 4:
      return 'rejected';
    case 0:
    default:
      return 'undecided';
  }
};

export const getOfferVendorName = (
  offer: ScreeningOfferLike,
  intl: IntlShape
) => {
  return (
    offer.vendor_information?.contact_name ||
    offer.offeror_identity_information?.name ||
    offer.created_by_name ||
    intl.formatMessage({ id: 'offers-offer-number' }, { id: offer.id })
  );
};

export const getOfferVendorCompany = (offer: ScreeningOfferLike) => {
  return (
    offer.vendor_information?.company_name ||
    offer.offeror_identity_information?.company ||
    '-'
  );
};

export const getOfferVendorEmail = (offer: ScreeningOfferLike) => {
  return (
    offer.vendor_information?.contact_email ||
    offer.vendor_information?.company_main_email ||
    offer.offeror_identity_information?.email ||
    '-'
  );
};

export const getVoucherId = (voucher: ScreeningVoucherLike) => {
  return Number(voucher.tender_voucher_id ?? voucher.id);
};

// Demo delivery logic.
// This should be replaced with backend-provided screening score when API is ready.
export const isVoucherDelivered = (
  voucher: ScreeningVoucherLike,
  response?: ScreeningVoucherResponseLike
) => {
  if (!response) return false;

  const voucherType = voucher.voucher_type;

  if (voucherType === 1) {
    return response.sum !== null && response.sum !== undefined;
  }

  if (voucherType === 3) {
    return !!response.attachments?.length;
  }

  if (voucherType === 4) {
    return response.is_approved !== null && response.is_approved !== undefined;
  }

  return (
    response.sum !== null ||
    response.is_approved !== null ||
    !!response.attachments?.length
  );
};

export const getDeliveryScoreColor = (score: ScreeningScore) => {
  if (score.total === 0) return '#8c8c8c';

  if (score.delivered === score.total) {
    return '#52c41a';
  }

  if (score.delivered === 0) {
    return '#ff4d4f';
  }

  return '#faad14';
};

export const buildMergedScreeningVouchers = (
  vouchers: ScreeningVoucherLike[],
  responses: ScreeningVoucherResponseLike[],
  intl: IntlShape
): ScreeningMergedVoucher[] => {
  const responsesByVoucherId = new Map<number, ScreeningVoucherResponseLike>();

  (responses || []).forEach((response) => {
    if (response?.voucher_id !== null && response?.voucher_id !== undefined) {
      responsesByVoucherId.set(Number(response.voucher_id), response);
    }
  });

  return (vouchers || []).map((voucher) => {
    const voucherId = getVoucherId(voucher);
    const response = responsesByVoucherId.get(voucherId);

    return {
      id: voucherId,
      name:
        voucher.name ||
        intl.formatMessage({ id: 'offers-voucher-number' }, { id: voucherId }),
      description: voucher.description,
      voucherType: voucher.voucher_type,
      isOptional: !!voucher.is_optional,
      isDelivered: isVoucherDelivered(voucher, response),
      projectAttachments: voucher.attachments || [],
      vendorAttachments: response?.attachments || [],
      unitCount: voucher.unit_count,
      unitType: voucher.unit_type,
      response,
    };
  });
};

export const calculateScreeningScores = (
  vouchers: ScreeningVoucherLike[],
  responses: ScreeningVoucherResponseLike[]
): ScreeningScores => {
  const mergedVouchers = buildMergedScreeningVouchers(vouchers, responses);

  return mergedVouchers.reduce<ScreeningScores>(
    (acc, voucher) => {
      const target = voucher.isOptional ? acc.optional : acc.required;

      target.total += 1;

      if (voucher.isDelivered) {
        target.delivered += 1;
      }

      return acc;
    },
    {
      required: {
        delivered: 0,
        total: 0,
      },
      optional: {
        delivered: 0,
        total: 0,
      },
    }
  );
};
